import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { HudTransitionMode } from '../content';

const containerVariants = {
  hidden: { opacity: 0, y: 20, filter: 'blur(5px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.85, ease: [0.2, 1, 0.32, 1] }
  }
} as const;

export const TransitionLayer = ({
  label,
  mode = 'scanner'
}: {
  label: string;
  mode?: HudTransitionMode;
}) => {
  const reducedMotion = useReducedMotion();
  const mobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 960px)').matches;

  return (
    <motion.div
      variants={containerVariants}
      initial={mobileViewport ? false : 'hidden'}
      whileInView={mobileViewport ? undefined : 'show'}
      animate={mobileViewport ? 'show' : undefined}
      viewport={mobileViewport ? undefined : { once: true, amount: 0.7 }}
      className="relative z-20 mx-auto h-28 w-[min(94vw,90rem)] overflow-hidden rounded-xl border border-brand-lime/25 bg-brand-panel/80"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(183,255,106,0.1),transparent_70%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(183,255,106,0.05)_1px,transparent_1px)] bg-[length:100%_6px] opacity-25" />

      {mode === 'radial' ? (
        <>
          <motion.div
            className="absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand-lime/50"
            animate={reducedMotion ? undefined : { scale: [0.3, 2.4], opacity: [0.8, 0] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: 'easeOut' }}
          />
          <motion.div
            className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand-aqua/50"
            animate={reducedMotion ? undefined : { scale: [0.6, 2], opacity: [0.6, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut', delay: 0.4 }}
          />
        </>
      ) : null}

      {mode === 'route' ? (
        <>
          <motion.div
            className="absolute inset-y-0 left-0 w-[18%] bg-[linear-gradient(90deg,rgba(183,255,106,0),rgba(183,255,106,0.25),rgba(183,255,106,0))]"
            animate={reducedMotion ? undefined : { x: ['-20%', '560%'] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-brand-aqua"
            animate={reducedMotion ? undefined : { x: ['-5%', '1040%'], opacity: [0.2, 1, 0.2] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
          />
        </>
      ) : null}

      {mode === 'lock' ? (
        <>
          <motion.div
            className="absolute inset-y-0 left-[20%] w-px bg-brand-lime/40"
            animate={reducedMotion ? undefined : { opacity: [0.25, 1, 0.3] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
          <motion.div
            className="absolute inset-y-0 right-[20%] w-px bg-brand-lime/40"
            animate={reducedMotion ? undefined : { opacity: [1, 0.25, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
          <motion.div
            className="absolute left-[20%] right-[20%] top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-brand-lime/20 via-brand-lime/70 to-brand-lime/20"
            animate={reducedMotion ? undefined : { opacity: [0.25, 1, 0.25] }}
            transition={{ duration: 1.1, repeat: Infinity }}
          />
        </>
      ) : null}

      {mode === 'scanner' ? (
        <motion.div
          className="absolute inset-x-0 top-0 h-[34%] bg-[linear-gradient(180deg,rgba(183,255,106,0.2),transparent)]"
          animate={reducedMotion ? undefined : { y: ['-100%', '320%'], opacity: [0.14, 0.4, 0.16] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: 'linear' }}
        />
      ) : null}

      <motion.div
        className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(88,247,182,0.22),transparent)]"
        animate={reducedMotion ? undefined : { x: ['-118%', '118%'], opacity: [0.15, 0.32, 0.16] }}
        transition={{ duration: 3.1, repeat: Infinity, ease: 'linear' }}
      />

      <motion.div
        className="absolute inset-y-0 left-[-30%] w-[30%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.35),rgba(88,247,182,0.2),transparent)]"
        animate={reducedMotion ? undefined : { x: ['-4%', '500%'], opacity: [0.08, 0.45, 0.1] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: 'linear' }}
      />

      <div className="relative h-full">
        <div className="absolute inset-y-0 left-4 hidden items-center sm:flex sm:left-6 md:left-8">
          <motion.span
            className="hud-micro whitespace-nowrap text-brand-text-3"
            animate={reducedMotion ? undefined : { opacity: [0.55, 1, 0.55] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          >
            STATE TRANSITION
          </motion.span>
        </div>

        <div className="absolute inset-0 flex items-center justify-center px-3 sm:px-24">
          <span className="hud-chip max-w-full text-center leading-tight whitespace-normal sm:whitespace-nowrap">{label}</span>
        </div>

        <div className="absolute right-3 top-2 sm:inset-y-0 sm:right-6 sm:flex sm:items-center md:right-8">
          <motion.span
            className="hud-micro whitespace-nowrap text-brand-lime"
            animate={reducedMotion ? undefined : { opacity: [1, 0.4, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          >
            SYNCED
          </motion.span>
        </div>
      </div>
    </motion.div>
  );
};
